import React, { useMemo, useState } from "react";
import ButtonLoading from "./ButtonLoading";
import useWriteGuard from "../hooks/useWriteGuard";
import { FiArrowLeft, FiArrowRight, FiCheck } from "react-icons/fi";
import { userRequest } from "../utils/requestMethods";
import {
  createClassGroup,
  deleteClassGroup,
  linkSubclassesToClassGroup,
  setClassLevelDefaultTuition,
  extractApiErrorMessage,
} from "../utils/classGroupApi";
import { DEFAULT_ARM_LABEL, pluralize } from "../utils/classGroupHelpers";
import { toast } from "react-toastify";
import { useStateContext } from "../contexts/ContextProvider";

const compactName = (name) => (name || "").toLowerCase().replace(/\s+/g, "");

const AddClassModal = ({
  closeModal,
  class_name,
  setClassname,
  price1,
  setPrice1,
  price2,
  setPrice2,
  price3,
  setPrice3,
  level_of_education,
  setLevelOfEducation,
  next_class,
  setNextClass,
  is_last_class,
  setIsLastClass,
  classes,
  submitHandler,
  loading: parentLoading,
  error: parentError,
  success: parentSuccess,
  subclassLabel = DEFAULT_ARM_LABEL,
  // 'single' adds one class; 'grouped' creates a new class level with several arms.
  // Fixed per open — the Classes page decides which flow the admin is in.
  initialMode = "single",
  onGroupCreated,
}) => {
  const { school } = useStateContext();
  const schoolID = school?._id;
  const { isWriteBlocked, guardWrite } = useWriteGuard();
  const creationMode = initialMode === "grouped" ? "grouped" : "single";
  const armsLabel = pluralize(subclassLabel, 2);

  // Grouped mode state
  const [groupName, setGroupName] = useState("");
  const [subclassLabelsInput, setSubclassLabelsInput] = useState("A, B, C");
  const [groupedLoading, setGroupedLoading] = useState(false);
  const [groupedError, setGroupedError] = useState("");
  const [groupedSuccess, setGroupedSuccess] = useState(false);
  const [groupStep, setGroupStep] = useState(1);
  // Where students in the new arms go at the end of the session: 'next' | 'graduate' | 'later'.
  // Without this, grouped arms were created with no next class and couldn't be promoted.
  const [promotionMode, setPromotionMode] = useState("later");
  // Once the admin picks an option themselves, Continue no longer overrides it.
  const [promotionTouched, setPromotionTouched] = useState(false);
  const [armNextClass, setArmNextClass] = useState({});

  const subclassLabels = useMemo(
    () =>
      subclassLabelsInput
        .split(",")
        .map((label) => label.trim())
        .filter(Boolean),
    [subclassLabelsInput],
  );

  // "JSS 1" -> "JSS 2", used to pre-pick JSS 2A for JSS 1A when those classes exist.
  const nextLevelName = useMemo(() => {
    const match = groupName.trim().match(/^(.*?)(\d+)$/);
    return match ? `${match[1]}${Number(match[2]) + 1}` : null;
  }, [groupName]);

  const suggestNextClass = (label) => {
    if (!nextLevelName) return "";
    const target = compactName(`${nextLevelName}${label}`);
    return classes?.find((cls) => compactName(cls.class_name) === target)?._id || "";
  };

  const goToGroupStep = (step) => {
    setGroupedError("");
    setGroupStep(step);
  };

  const continueGroupSetup = () => {
    if (!groupName.trim()) {
      setGroupedError("Give this class level a name to continue.");
      return;
    }
    if (subclassLabels.length === 0) {
      setGroupedError(`Add the ${armsLabel.toLowerCase()} you want to create.`);
      return;
    }
    const duplicateLabels = subclassLabels.filter(
      (label, index) => subclassLabels.indexOf(label) !== index,
    );
    if (duplicateLabels.length > 0) {
      setGroupedError(`Each ${subclassLabel.toLowerCase()} label must be unique.`);
      return;
    }

    // Until the admin has made their own choice, re-derive suggestions from the current
    // name/labels (they may have gone Back and changed them); after that, keep their picks.
    const suggestions = {};
    subclassLabels.forEach((label) => {
      suggestions[label] = promotionTouched
        ? armNextClass[label] || ""
        : suggestNextClass(label);
    });
    setArmNextClass(suggestions);
    if (!promotionTouched) {
      setPromotionMode(Object.values(suggestions).some(Boolean) ? "next" : "later");
    }
    goToGroupStep(2);
  };

  const guardedSubmitHandler = (e) => {
    e.preventDefault();
    if (!guardWrite()) return;

    // Pressing Enter on step 1 should go to step 2, not create everything early.
    if (creationMode === "grouped" && groupStep === 1) {
      continueGroupSetup();
      return;
    }

    if (creationMode === "grouped") {
      handleGroupedSubmit(e);
    } else {
      submitHandler(e);
    }
  };

  // Grouped mode submission:
  // 1. Create class group
  // 2. Create each subclass via /classes
  // 3. Link them via /class-groups/:schoolId/:classGroupId/subclasses
  //
  // POST /classes returns the newly created class directly as `data` (see
  // school-api AddClass.ts: `return savedClass.classes[savedClass.classes.length - 1]`,
  // wrapped by successResponse as `{ data: <class> }`) — no need to search the class
  // list by name, which is unsafe if two labels produce the same class_name.
  //
  // If an arm fails to create partway through, the arms that WERE created and the
  // (still empty) group are both deleted, so a failed attempt leaves nothing behind.
  const handleGroupedSubmit = async (e) => {
    e.preventDefault();
    if (!groupName.trim()) {
      setGroupedError("Class level name is required");
      return;
    }
    if (!price1 || !price2 || !price3 || !level_of_education) {
      setGroupedError("Please fill all fees and level of education fields");
      return;
    }
    if (promotionMode === "next" && subclassLabels.some((label) => !armNextClass[label])) {
      setGroupedError(
        `Choose the next class for each ${subclassLabel.toLowerCase()}, or pick "Decide later".`,
      );
      return;
    }

    const labels = subclassLabels;

    if (labels.length === 0) {
      setGroupedError(`Please provide at least one ${subclassLabel.toLowerCase()} label`);
      return;
    }

    const duplicateLabels = labels.filter((label, i) => labels.indexOf(label) !== i);
    if (duplicateLabels.length > 0) {
      setGroupedError(
        `${subclassLabel} labels must be unique — duplicate: ${[...new Set(duplicateLabels)].join(", ")}`,
      );
      return;
    }

    let classGroupId = null;
    const createdClasses = [];
    let groupLinked = false;

    try {
      setGroupedLoading(true);
      setGroupedError("");

      // Step 1: Create class group
      const createdGroup = await createClassGroup(schoolID, groupName.trim());
      classGroupId = createdGroup._id;

      // Step 2: Create each subclass, reading the created class straight off the
      // response instead of searching for it by name.
      for (const label of labels) {
        const fullClassName = `${groupName.trim()} ${label}`.trim();
        const { data: body } = await userRequest.post("/classes", {
          schoolId: schoolID,
          class_name: fullClassName,
          class_price: {
            1: String(price1),
            2: String(price2),
            3: String(price3),
          },
          level_of_education,
          is_last_class: promotionMode === "graduate",
          ...(promotionMode === "next" && armNextClass[label]
            ? { next_class: armNextClass[label] }
            : {}),
        });

        const newClass = body;
        if (!newClass?._id) {
          throw new Error(`Class "${fullClassName}" was created but its id was not returned`);
        }

        createdClasses.push({ class_id: newClass._id, group_label: label });
      }

      // Step 3: Link subclasses to class group
      await linkSubclassesToClassGroup(schoolID, classGroupId, createdClasses);
      groupLinked = true;

      // The fees entered in this wizard are the group's starting default, not merely
      // matching per-arm prices. Persisting this keeps the Fees & Add-ons view and
      // future arm creation in sync with what the admin just configured.
      await setClassLevelDefaultTuition(schoolID, classGroupId, {
        1: String(price1),
        2: String(price2),
        3: String(price3),
      });

      setGroupedSuccess(true);
      toast.success(
        `Created "${groupName.trim()}" with ${createdClasses.length} ${pluralize(subclassLabel, createdClasses.length).toLowerCase()}`,
      );

      if (onGroupCreated) onGroupCreated(classGroupId);

      setTimeout(() => {
        closeModal();
      }, 1500);
    } catch (err) {
      console.error("Error creating grouped classes:", err);

      // Best-effort cleanup so a failed attempt never leaves half-created arms or an
      // empty group behind for the admin to discover later.
      let groupRemoved = false;
      if (!groupLinked) {
        if (createdClasses.length > 0) {
          await Promise.allSettled(
            createdClasses.map((c) => userRequest.delete(`/classes/${schoolID}/${c.class_id}`)),
          );
        }
        if (classGroupId) {
          groupRemoved = await deleteClassGroup(schoolID, classGroupId)
            .then(() => true)
            .catch(() => false);
        }
      }

      const baseMessage = extractApiErrorMessage(err, "Failed to create the class level");
      setGroupedError(
        groupLinked
          ? `${baseMessage} The class level and its ${armsLabel.toLowerCase()} were created, but the default fees could not be saved. Open the group's Fees tab to set them.`
          : classGroupId && !groupRemoved
            ? `${baseMessage} Nothing was kept except an empty group "${groupName.trim()}", which you can delete from the Class Groups view.`
            : `${baseMessage} Nothing was created — you can try again.`,
      );
    } finally {
      setGroupedLoading(false);
    }
  };

  const currentLoading = creationMode === "grouped" ? groupedLoading : parentLoading;
  const currentError = creationMode === "grouped" ? groupedError : parentError;
  const currentSuccess = creationMode === "grouped" ? groupedSuccess : parentSuccess;

  return (
    <div>
      <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40" onClick={closeModal} />

      {/* Bottom sheet on mobile, centered dialog on sm+ */}
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4">
        <div className="relative w-full sm:max-w-xl flex flex-col max-h-[90vh]">
          <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl border border-slate-200 flex flex-col max-h-[90vh]">
            {/* Header — sticky */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 flex-shrink-0">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  {creationMode === "grouped" ? "Create a new class level" : "Add Class"}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {creationMode === "grouped"
                    ? `Creates the class level and all its ${armsLabel.toLowerCase()} in one go`
                    : "Create a new class and set term fees"}
                </p>
              </div>
              <button
                type="button"
                className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center"
                onClick={closeModal}
              >
                <svg
                  className="w-4 h-4"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 14 14"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                  />
                </svg>
                <span className="sr-only">Close modal</span>
              </button>
            </div>

            {/* Scrollable body */}
            <div className="overflow-y-auto flex-1 p-5">
              {currentError && (
                <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  {currentError}
                </div>
              )}
              {currentSuccess && (
                <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                  {creationMode === "grouped"
                    ? `Class level and ${armsLabel.toLowerCase()} created successfully`
                    : "New class added successfully"}
                </div>
              )}

              <form onSubmit={guardedSubmitHandler} className="space-y-4">
                {creationMode === "single" ? (
                  /* Single Class Form */
                  <>
                    <div>
                      <label
                        htmlFor="class_name"
                        className="block text-sm font-medium text-slate-700"
                      >
                        Name of Class
                      </label>
                      <input
                        id="class_name"
                        type="text"
                        onChange={(e) => setClassname(e.target.value)}
                        value={class_name}
                        className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                        placeholder="e.g. JSS 1"
                      />
                    </div>
                  </>
                ) : (
                  /* Guided grouped-class setup */
                  <div className="space-y-4">
                    <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-3">
                      <div className="flex items-center gap-2 text-xs font-semibold text-indigo-900">
                        <span className={`flex h-5 w-5 items-center justify-center rounded-full ${groupStep === 1 ? "bg-indigo-600 text-white" : "bg-emerald-500 text-white"}`}>
                          {groupStep === 1 ? "1" : <FiCheck className="h-3 w-3" />}
                        </span>
                        Name and {armsLabel.toLowerCase()}
                        <span className="h-px flex-1 bg-indigo-200" />
                        <span className={`flex h-5 w-5 items-center justify-center rounded-full ${groupStep === 2 ? "bg-indigo-600 text-white" : "bg-white text-indigo-500 border border-indigo-200"}`}>2</span>
                        Fees and next class
                      </div>
                    </div>

                    {groupStep === 1 ? (
                      <>
                        <div>
                          <label htmlFor="group_name_input" className="block text-sm font-semibold text-slate-800">
                            What is the class level called?
                          </label>
                          <p className="mt-0.5 text-xs text-slate-500">
                            The part every {subclassLabel.toLowerCase()} shares — e.g. "JSS 1" for JSS 1A, JSS 1B and JSS 1C.
                          </p>
                          <input
                            id="group_name_input"
                            type="text"
                            autoFocus
                            value={groupName}
                            onChange={(e) => setGroupName(e.target.value)}
                            className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                            placeholder="e.g. Primary 1 or JSS 1"
                          />
                        </div>

                        <div>
                          <label htmlFor="subclass_labels_input" className="block text-sm font-semibold text-slate-800">
                            Which {armsLabel.toLowerCase()} does it have?
                          </label>
                          <p className="mt-0.5 text-xs text-slate-500">Separate labels with commas. For example: A, B, C or Gold, Silver.</p>
                          <input
                            id="subclass_labels_input"
                            type="text"
                            value={subclassLabelsInput}
                            onChange={(e) => setSubclassLabelsInput(e.target.value)}
                            className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                            placeholder="A, B, C"
                          />
                        </div>

                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3.5">
                          <p className="text-xs font-semibold text-slate-700">Preview</p>
                          {groupName.trim() && subclassLabels.length > 0 ? (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {subclassLabels.map((label) => (
                                <span key={label} className="rounded-lg border border-indigo-100 bg-white px-2.5 py-1 text-xs font-medium text-indigo-800">
                                  {groupName.trim()} {label}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className="mt-1 text-xs text-slate-500">Your new classes will appear here before anything is created.</p>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600">
                        <p className="font-semibold text-slate-800">
                          You are creating {subclassLabels.length}{" "}
                          {pluralize(subclassLabel, subclassLabels.length).toLowerCase()}:{" "}
                          {subclassLabels.map((label) => `${groupName.trim()} ${label}`).join(", ")}
                        </p>
                        <p className="mt-1">
                          These fees become the group's default for every{" "}
                          {subclassLabel.toLowerCase()}. You can still give one{" "}
                          {subclassLabel.toLowerCase()} a different price later.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Term Fees (Shared across single and grouped modes) */}
                {(creationMode === "single" || groupStep === 2) && <div>
                  <label className="block text-sm font-medium text-slate-700">
                    {creationMode === "grouped" ? "Default Term Fees" : "Term Fees"}
                  </label>
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    <div>
                      <label htmlFor="price1" className="block text-xs font-medium text-slate-500">
                        1st Term
                      </label>
                      <input
                        type="number"
                        id="price1"
                        onChange={(e) => setPrice1(e.target.value)}
                        defaultValue={price1}
                        className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                        placeholder="20000"
                      />
                    </div>
                    <div>
                      <label htmlFor="price2" className="block text-xs font-medium text-slate-500">
                        2nd Term
                      </label>
                      <input
                        type="number"
                        id="price2"
                        onChange={(e) => setPrice2(e.target.value)}
                        defaultValue={price2}
                        className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                        placeholder="20000"
                      />
                    </div>
                    <div>
                      <label htmlFor="price3" className="block text-xs font-medium text-slate-500">
                        3rd Term
                      </label>
                      <input
                        type="number"
                        id="price3"
                        onChange={(e) => setPrice3(e.target.value)}
                        defaultValue={price3}
                        className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                        placeholder="20000"
                      />
                    </div>
                  </div>
                </div>}

                {(creationMode === "single" || groupStep === 2) && <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-slate-700">
                      Level of Education
                    </label>
                    <select
                      onChange={(e) => setLevelOfEducation(e.target.value)}
                      value={level_of_education}
                      className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                    >
                      <option value="">Select</option>
                      <option value="pre_school">Pre-School</option>
                      <option value="kindergarten">Kindergarten</option>
                      <option value="nursery">Nursery</option>
                      <option value="primary">Primary</option>
                      <option value="secondary">Secondary</option>
                    </select>
                  </div>

                  {creationMode === "single" && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700">Next Class</label>
                      <select
                        onChange={(e) => setNextClass(e.target.value)}
                        value={next_class}
                        className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                      >
                        <option value="">Select Next Class</option>
                        {classes?.map((cls) => (
                          <option key={cls._id} value={cls._id}>
                            {cls.class_name}
                          </option>
                        ))}
                      </select>
                      {next_class && (
                        <p className="mt-2 text-xs text-blue-600">
                          Students will progress to{" "}
                          {classes?.find((cls) => cls._id === next_class)?.class_name} after
                          completing this class
                        </p>
                      )}
                    </div>
                  )}
                </div>}

                {creationMode === "grouped" && groupStep === 2 && (
                  <div className="space-y-3 rounded-xl border border-slate-200 p-4">
                    <div>
                      <p className="text-sm font-medium text-slate-700">
                        When students finish these {armsLabel.toLowerCase()}, where do they go?
                      </p>
                      <p className="mt-0.5 text-xs text-slate-500">
                        Used when you promote students at the end of the session.
                      </p>
                    </div>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                      {[
                        { value: "next", label: "Move up a class" },
                        { value: "graduate", label: "They graduate" },
                        { value: "later", label: "Decide later" },
                      ].map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => {
                            setPromotionMode(opt.value);
                            setPromotionTouched(true);
                          }}
                          className={`rounded-lg border px-3 py-2 text-xs font-semibold transition-colors ${
                            promotionMode === opt.value
                              ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                              : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>

                    {promotionMode === "next" && (
                      <div className="space-y-2">
                        {subclassLabels.map((label) => (
                          <div key={label} className="flex items-center gap-2">
                            <span className="w-28 flex-shrink-0 truncate text-sm font-medium text-slate-700">
                              {groupName.trim()} {label}
                            </span>
                            <FiArrowRight className="h-4 w-4 flex-shrink-0 text-slate-400" />
                            <select
                              value={armNextClass[label] || ""}
                              onChange={(e) => {
                                setArmNextClass((prev) => ({ ...prev, [label]: e.target.value }));
                                setPromotionTouched(true);
                              }}
                              className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                            >
                              <option value="">Choose next class</option>
                              {classes?.map((cls) => (
                                <option key={cls._id} value={cls._id}>
                                  {cls.class_name}
                                </option>
                              ))}
                            </select>
                          </div>
                        ))}
                        {classes?.length === 0 && (
                          <p className="text-xs text-slate-500">
                            You have no other classes yet. Choose "Decide later" and set this once
                            the next class exists.
                          </p>
                        )}
                      </div>
                    )}

                    {promotionMode === "later" && (
                      <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        Students in these {armsLabel.toLowerCase()} can't be promoted until this
                        is set. You can set it later from each {subclassLabel.toLowerCase()}'s
                        Edit Class Details.
                      </p>
                    )}
                  </div>
                )}

                {creationMode === "single" && (
                  <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="mr-3">
                      <label className="block text-sm font-medium text-slate-800">
                        Is Last Class
                      </label>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {is_last_class
                          ? "This is the final class in the progression"
                          : "Students can progress to another class after this"}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsLastClass(!is_last_class)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        is_last_class ? "bg-blue-600" : "bg-slate-300"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          is_last_class ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                )}

                {/* Footer */}
                <div className="flex items-center justify-between gap-2 border-t border-slate-100 pt-4 pb-1">
                  {creationMode === "grouped" && groupStep === 2 ? (
                    <button type="button" onClick={() => goToGroupStep(1)} className="inline-flex items-center gap-1 rounded-full px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">
                      <FiArrowLeft className="h-4 w-4" /> Back
                    </button>
                  ) : <span />}
                  <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  {creationMode === "grouped" && groupStep === 1 ? (
                    <button type="button" onClick={continueGroupSetup} className="inline-flex items-center gap-1 rounded-full bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-700">
                      Continue <FiArrowRight className="h-4 w-4" />
                    </button>
                  ) : currentLoading ? (
                    <ButtonLoading styles="text-white bg-blue-600 focus:outline-none font-medium rounded-full text-sm px-5 py-2.5 text-center" />
                  ) : (
                    <button
                      type="submit"
                      disabled={isWriteBlocked}
                      className="text-white bg-[#3F3DA5] hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-full text-sm px-5 py-2.5"
                    >
                      {creationMode === "grouped" ? "Create class level" : "Add New Class"}
                    </button>
                  )}
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddClassModal;
